
/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Select, InputNumber, Space, Spin } from 'antd';
import axios from 'axios';
import { BASE_URL } from '../api';
import { toast } from 'react-toastify';
import App from '../App.css';
import { openOrderSuccess } from './OrderSuccessModal';
import { cleanBybitFromMessage } from '../utils/messageUtils';

// --- normalizeQuantity.js logic (inline for browser use) ---
function countDecimals(str) {
  const s = String(str);
  const dot = s.indexOf(".");
  return dot === -1 ? 0 : (s.length - dot - 1);
}
function toScaledBigInt(value, scale) {
  const s = String(value);
  const decs = countDecimals(s);
  if (decs === scale) return BigInt(s.replace(".", ""));
  if (decs < scale) {
    const zeros = "0".repeat(scale - decs);
    return BigInt((s.includes(".") ? s.replace(".", "") : s) + zeros);
  }
  const [intPart, fracPart = ""] = s.split(".");
  const keep = fracPart.slice(0, scale);
  const drop = fracPart.slice(scale);
  let core = BigInt((intPart || "0") + (keep || "").padEnd(scale, "0"));
  if (drop && /[1-9]/.test(drop)) {
    core += 1n;
  }
  return core;
}
function ceilToStep(n, step) {
  return ((n + (step - 1n)) / step) * step;
}
async function normalizeQuantity(symbol, desiredQuantity) {
  if (!symbol || desiredQuantity == null) {
    throw new Error("symbol and desiredQuantity are required.");
  }
  const url = new URL("https://api.bybit.com/v5/market/instruments-info");
  url.searchParams.set("category", "linear");
  url.searchParams.set("symbol", symbol);
  let data;
  try {
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    data = await res.json();
  } catch (err) {
    throw new Error(`Failed to fetch Bybit instruments info: ${err.message}`);
  }
  if (data?.retCode !== 0 || !data?.result?.list?.length) {
    throw new Error(`Unexpected API response: retCode=${data?.retCode}`);
  }
  const lot = data.result.list[0]?.lotSizeFilter;
  if (!lot) throw new Error("lotSizeFilter not found for symbol.");
  const minOrderQtyStr = lot.minOrderQty;
  const qtyStepStr     = lot.qtyStep;
  if (minOrderQtyStr == null || qtyStepStr == null) {
    throw new Error("minOrderQty or qtyStep missing in lotSizeFilter.");
  }
  const decMin  = countDecimals(minOrderQtyStr);
  const decStep = countDecimals(qtyStepStr);
  const scale   = Math.max(decMin, decStep);
  const stepScaled = toScaledBigInt(qtyStepStr, scale);
  if (stepScaled <= 0n) throw new Error("Invalid qtyStep <= 0.");
  const minScaled = toScaledBigInt(minOrderQtyStr, scale);
  let desiredScaled = toScaledBigInt(desiredQuantity, scale);
  let adjustedScaled = ceilToStep(desiredScaled, stepScaled);
  const minAligned = ceilToStep(minScaled, stepScaled);
  if (adjustedScaled < minAligned) adjustedScaled = minAligned;
  const adjustedStr = adjustedScaled.toString();
  let result;
  if (scale === 0) {
    result = Number(adjustedStr);
  } else {
    const intPart = adjustedStr.slice(0, -scale) || "0";
    const fracPart = adjustedStr.slice(-scale).padStart(scale, "0");
    result = Number(`${intPart}.${fracPart}`);
  }
  return result;
}


const { Option } = Select;

const orderTypeOptions = [
  { label: 'Market', value: 'Market' },
  { label: 'Limit', value: 'Limit' }
];

const sideOptions = [
  { label: 'Long', value: 'Buy' },
  { label: 'Short', value: 'Sell' }
];

const ACCOUNT_VALUE = parseFloat(process.env.REACT_APP_ACCOUNT_VALUE) || 3000;
const OrderForm = ({ 
  visible, 
  onClose, 
  onOrderSuccess, 
  editingOrder = null,
  dcaOrder = null 
}) => {
  const [form] = Form.useForm();
  const [algoForm] = Form.useForm();
  const [AlgorithmOptions, setAlgorithmOptions] = useState([]);
  const [algoModalOpen, setAlgoModalOpen] = useState(false);
  const [algoLoading, setAlgoLoading] = useState(false);
  const [algoError, setAlgoError] = useState('');

  // Price calculation states
  const [ninetyPercentPrice, setNinetyPercentPrice] = useState('');
  const [ninetyFivePercentPrice, setNinetyFivePercentPrice] = useState('');
  const [showMarketSuggestion, setShowMarketSuggestion] = useState(false);

    // Fetch algorithms for Signal By dropdown
  const fetchAlgorithms = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/algorithms`, { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) {
        setAlgorithmOptions(
          data.map(a => ({
            label: `${a.name} - ${a.category} - ${a.risk}`,
             _id: a._id,
            value: a.name,
            category: a.category,
            risk: a.risk
          }))
        );
      }
    } catch (err) {
      console.error('Error fetching algorithms:', err);
      setAlgorithmOptions([]);
    }
  };

  // --- Add New Algorithm modal handlers ---
  const handleAddAlgorithm = () => {
    setAlgoModalOpen(true);
    setAlgoError('');
    algoForm.resetFields();
  };

  const handleAlgoModalCancel = () => {
    setAlgoModalOpen(false);
    setAlgoError('');
    algoForm.resetFields();
  };

  const handleAlgoFinish = async (values) => {
    setAlgoLoading(true);
    setAlgoError('');
    try {
      const { name, category, risk } = values;
      if (!name || /[^a-zA-Z0-9]/.test(name)) {
        setAlgoError('Name must be alphanumeric with no spaces or special characters.');
        setAlgoLoading(false);
        return;
      }
      if (!category) {
        setAlgoError('Category is required.');
        setAlgoLoading(false);
        return;
      }
      if (typeof risk !== 'number' || isNaN(risk) || risk < 0.01 || risk > 5) {
        setAlgoError('Risk must be a number between 0.01 and 5.');
        setAlgoLoading(false);
        return;
      }
      
      const res = await fetch(`${BASE_URL}/api/algorithms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, category, risk }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data && data.name) {
        toast.success('Algorithm added!');
        setAlgoModalOpen(false);
        await fetchAlgorithms();
        form.setFieldsValue({ algorithm: data.name, tradeRisk: data.risk || 1 });
        setTradeRisk(data.risk || 1);
      } else {
        setAlgoError(data.error || 'Failed to add algorithm.');
      }
    } catch (err) {
      setAlgoError('Failed to add algorithm.');
    } finally {
      setAlgoLoading(false);
    }
  };

  useEffect(() => {
    fetchAlgorithms();
  }, []);

  const [positionIdx, setPositionIdx] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tokenOptions, setTokenOptions] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [lastPrice, setLastPrice] = useState('');
  const [count, setCount] = useState('');
  const [totalValue, setTotalValue] = useState('');
  const [tradeRisk, setTradeRisk] = useState(1); // Default to 1
  const [stopLoss, setStopLoss] = useState('');
  const [isDcaOrder, setIsDcaOrder] = useState(false); // Add isDcaOrder state
  const [tokenLoading, setTokenLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false); // Separate loading state for parsing prompt

  // Calculate percentage prices based on reference price and side (long/short)
  const calculatePercentagePrices = (referencePrice) => {
    if (!referencePrice || isNaN(referencePrice)) {
      setNinetyPercentPrice('');
      setNinetyFivePercentPrice('');
      return;
    }
    
    const price = parseFloat(referencePrice);
    const side = form.getFieldValue('side');
    
    if (side === 'Sell') { // Short position
      const oneHundredTenPercent = (price * 1.10).toFixed(6);
      const oneHundredFivePercent = (price * 1.05).toFixed(6);
      setNinetyPercentPrice(oneHundredTenPercent); // Using same state but for 110%
      setNinetyFivePercentPrice(oneHundredFivePercent); // Using same state but for 105%
    } else { // Long position
      const ninetyPercent = (price * 0.90).toFixed(6);
      const ninetyFivePercent = (price * 0.95).toFixed(6);
      setNinetyPercentPrice(ninetyPercent);
      setNinetyFivePercentPrice(ninetyFivePercent);
    }
  };

  // Validate stop loss based on position side
  const validateStopLoss = (stopLossValue, referencePrice) => {
    if (!stopLossValue || !referencePrice) return true;
    
    const stopLoss = parseFloat(stopLossValue);
    const reference = parseFloat(referencePrice);
    const side = form.getFieldValue('side');
    
    if (side === 'Sell') { // Short position
      const oneHundredTenPercent = reference * 1.10;
      return stopLoss < oneHundredTenPercent; // Stop loss should be below 110%
    } else { // Long position
      const ninetyPercent = reference * 0.90;
      return stopLoss > ninetyPercent; // Stop loss should be above 90%
    }
  };

  // Fetch Bybit tokens for dropdown (on demand)
  const fetchTokenOptions = async () => {
    setTokenLoading(true);
    try {
      const res = await axios.get('https://api.bybit.com/v5/market/tickers?category=linear');
      const list = res.data?.result?.list || [];
      const filtered = list.filter(t => t.symbol.endsWith('USDT'));
      setTokenOptions(filtered.map(t => ({
        label: t.symbol,
        value: t.symbol,
        lastPrice: t.lastPrice
      })));
    } catch (err) {
      setTokenOptions([]);
      console.error('Error fetching tokens:', err);
    } finally {
      setTokenLoading(false);
    }
  };

  // Fetch tokens when modal is opened
  useEffect(() => {
    if (visible) {
      fetchTokenOptions();
    }
  }, [visible]);

  // Set form data when editing or creating DCA order
  useEffect(() => {
    if (editingOrder && visible) {
      setSelectedToken(editingOrder.token);
      const tokenObj = tokenOptions.find(t => t.value === editingOrder.token);
      if (tokenObj) {
        setLastPrice(tokenObj.lastPrice);
        calculatePercentagePrices(tokenObj.lastPrice);
      }
      setCount(editingOrder.count ? editingOrder.count.toString() : '');
      setTotalValue(
        editingOrder.count && tokenObj 
          ? (parseFloat(editingOrder.count) * parseFloat(tokenObj.lastPrice)).toFixed(4) 
          : ''
      );
      
      form.setFieldsValue({
        ...editingOrder,
        tradeRisk: editingOrder.tradeRisk || 1, // Default to 1 if not set
        totalValue: editingOrder.count && tokenObj 
          ? (parseFloat(editingOrder.count) * parseFloat(tokenObj.lastPrice)).toFixed(4) 
          : ''
      });

      // Special handling for stop loss order editing
      if (editingOrder.orderNature === 'sl') {
        form.setFieldsValue({
          ...editingOrder,
          orderType: 'Limit',
          entryPrice: editingOrder.stopLoss || '',
          stopLoss: editingOrder.stopLoss || '',
          tradeRisk: editingOrder.tradeRisk || 1,
          totalValue: editingOrder.count && tokenObj 
            ? (parseFloat(editingOrder.count) * parseFloat(tokenObj.lastPrice)).toFixed(4) 
            : ''
        });
      } else if (editingOrder.orderNature === 'tp') {
        form.setFieldsValue({
          ...editingOrder,
          orderType: 'Limit',
          entryPrice: editingOrder.takeProfit || '',
          tradeRisk: editingOrder.tradeRisk || 1,
          totalValue: editingOrder.count && tokenObj 
            ? (parseFloat(editingOrder.count) * parseFloat(tokenObj.lastPrice)).toFixed(4) 
            : ''
        });
      } else {
        form.setFieldsValue({
          ...editingOrder,
          tradeRisk: editingOrder.tradeRisk || 1,
          totalValue: editingOrder.count && tokenObj 
            ? (parseFloat(editingOrder.count) * parseFloat(tokenObj.lastPrice)).toFixed(4) 
            : ''
        });
      }
    } else if (dcaOrder && visible) {
      // Handle DCA order pre-filling
      setSelectedToken(dcaOrder.token);
      const tokenObj = tokenOptions.find(t => t.value === dcaOrder.token);
      if (tokenObj) {
        setLastPrice(tokenObj.lastPrice);
        calculatePercentagePrices(tokenObj.lastPrice);
      }
      
      // Pre-fill form with DCA order data (but keep entryPrice empty)
      form.setFieldsValue({
        token: dcaOrder.token,
        side: dcaOrder.side,
        orderType: dcaOrder.orderType,
        stopLoss: dcaOrder.stopLoss,
        algorithm: dcaOrder.Algorithm,
        positionIdx: dcaOrder.positionIdx,
        tradeRisk: 1, // Default trade risk
        entryPrice: '', // Keep entry price empty as requested
        isDcaOrder: true, // Set isDcaOrder to true for DCA orders
      });
      
      setStopLoss(dcaOrder.stopLoss || '');
      setIsDcaOrder(true); // Always set to true when opening DCA order
      setTradeRisk(1);
      setCount('');
      setTotalValue('');
    } else if (visible && !editingOrder && !dcaOrder) {
      // Reset form for new order
      form.resetFields();
      form.setFieldsValue({ 
        tradeRisk: 1, // Set default trade risk to 1
        isDcaOrder: false
      });
      setSelectedToken(null);
      setLastPrice('');
      setCount('');
      setTotalValue('');
      setTradeRisk(1); // Set default trade risk to 1
      setStopLoss('');
      setIsDcaOrder(false); // Reset isDcaOrder state
      setNinetyPercentPrice('');
      setNinetyFivePercentPrice('');
    }
  }, [editingOrder, dcaOrder, visible, tokenOptions, form]);

  // When token changes, update lastPrice and calculate percentages
  const handleTokenChange = (value) => {
    setSelectedToken(value);
    const token = tokenOptions.find(t => t.value === value);
    if (token) {
      setLastPrice(token.lastPrice);
      const orderType = form.getFieldValue('orderType');
      if (orderType === 'Market') {
        calculatePercentagePrices(token.lastPrice);
      }
    } else {
      setLastPrice('');
      setNinetyPercentPrice('');
      setNinetyFivePercentPrice('');
    }
    setCount('');
    setTotalValue('');
    form.setFieldsValue({ count: '', totalValue: '' });
  };

  // When order type changes, recalculate percentages
  const handleOrderTypeChange = (value) => {
    if (value === 'Market' && lastPrice) {
      calculatePercentagePrices(lastPrice);
    } else if (value === 'Limit') {
      const entryPrice = form.getFieldValue('entryPrice');
      if (entryPrice) {
        calculatePercentagePrices(entryPrice);
      } else {
        setNinetyPercentPrice('');
        setNinetyFivePercentPrice('');
      }
    }
  };

  // Verify stop loss price based on order side
  const stopLossVerifier = (currentPrice, inputPrice, side) => {
    if (!currentPrice || !inputPrice) return true;
    
    const current = parseFloat(currentPrice);
    const input = parseFloat(inputPrice);
    
    if (side === 'Buy') { // For Long position
      // Check if stop loss is above current price
      if (input > current) {
        toast.error('For Long position, stop loss cannot be above current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
      // Check if stop loss is too low (more than 25% below)
      const minAllowedPrice = current * 0.75;
      if (input < minAllowedPrice) {
        toast.error('For Long position, stop loss cannot be more than 25% below current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
    } else if (side === 'Sell') { // For Short position
      // Check if stop loss is below current price
      if (input < current) {
        toast.error('For Short position, stop loss cannot be below current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
      // Check if stop loss is too high (more than 25% above)
      const maxAllowedPrice = current * 1.25;
      if (input > maxAllowedPrice) {
        toast.error('For Short position, stop loss cannot be more than 25% above current price!', { 
          position: 'top-right', 
          autoClose: 5000 
        });
        return false;
      }
    }
    return true;
  };

  // Recalculate total order value and count based on risk and stop loss
  const recalcTotalValue = (newTradeRisk, newStopLoss, customEntryPrice) => {
    const riskPercent = parseFloat(newTradeRisk);
    const stopLossPrice = parseFloat(newStopLoss);
    // Use entryPrice if orderType is Limit and entryPrice is present, else use lastPrice
    let entryPrice = lastPrice;
    const orderType = form.getFieldValue('orderType');
    if (orderType === 'Limit') {
      const entry = customEntryPrice !== undefined ? customEntryPrice : form.getFieldValue('entryPrice');
      if (entry) entryPrice = entry;
    }
    entryPrice = parseFloat(entryPrice);

    // Only proceed with calculation if stop loss is provided and verified
    // if (newStopLoss) {
    //   // Verify stop loss before calculating
    //   if (!stopLossVerifier(entryPrice, stopLossPrice, form.getFieldValue('side'))) {
    //     setCount('');
    //     setTotalValue('');
    //     form.setFieldsValue({ count: '', totalValue: '' });
    //     return;
    //   }
    // }

    if (!isNaN(riskPercent) && !isNaN(stopLossPrice) && !isNaN(entryPrice) && 
        riskPercent > 0 && stopLossPrice > 0 && entryPrice > 0) {
      const riskAmount = ACCOUNT_VALUE * (riskPercent / 100);
      const riskPerShare = Math.abs(entryPrice - stopLossPrice);
      if (riskPerShare > 0) {
        const positionSize = riskAmount / riskPerShare;
        const totalTradeValue = positionSize * entryPrice;
        const formattedCount = positionSize.toFixed(4);
        const formattedTotal = totalTradeValue.toFixed(4);
        setCount(formattedCount);
        setTotalValue(formattedTotal);
        form.setFieldsValue({ 
          count: formattedCount, 
          totalValue: formattedTotal 
        });
      } else {
        setCount('');
        setTotalValue('');
        form.setFieldsValue({ count: '', totalValue: '' });
      }
    } else {
      setCount('');
      setTotalValue('');
      form.setFieldsValue({ count: '', totalValue: '' });
    }
  };

  const handleTotalValueChange = (val) => {
    setTotalValue(val);
    if (lastPrice && val) {
      const entryPrice = parseFloat(lastPrice);
      const totalTradeValue = parseFloat(val);
      if (!isNaN(entryPrice) && entryPrice > 0) {
        const positionSize = totalTradeValue / entryPrice;
        let formattedCount = positionSize;
        if (positionSize > 50) {
          formattedCount = Math.floor(positionSize);
        } else {
          formattedCount = positionSize.toFixed(4);
        }
        setCount(formattedCount.toString());
        form.setFieldsValue({ count: formattedCount.toString() });
      }
    }
  };

  const handleCountChange = (value) => {
    let displayValue = value;
    if (parseFloat(value) > 50) {
      displayValue = Math.floor(parseFloat(value)).toString();
    }
    setCount(displayValue);
    if (lastPrice && displayValue) {
      const entryPrice = parseFloat(lastPrice);
      const positionSize = parseFloat(displayValue);
      if (!isNaN(entryPrice) && entryPrice > 0) {
        const totalTradeValue = positionSize * entryPrice;
        const formattedTotal = totalTradeValue.toFixed(4);
        setTotalValue(formattedTotal);
        form.setFieldsValue({ totalValue: formattedTotal });
      }
    } else {
      setTotalValue('');
      form.setFieldsValue({ totalValue: '' });
    }
    form.setFieldsValue({ count: displayValue });
  };

  const handleTradeRiskChange = (val) => {
    const riskValue = val || 1; // Default to 1 if null/undefined
    setTradeRisk(riskValue);
    // Remove automatic calculation - will calculate on submit
  };

  // When entryPrice changes (for Limit orders), recalc using entryPrice and update percentages
  const handleEntryPriceChange = (e) => {
    const value = e.target.value;
    const orderType = form.getFieldValue('orderType');
    if (orderType === 'Limit') {
      calculatePercentagePrices(value);
    }
    // Remove automatic calculation - will calculate on submit
    // Check whether limit price is close to market price (±2%) and show suggestion
    const checkEntryPrice = (entryVal) => {
      if (!entryVal || !lastPrice) {
        setShowMarketSuggestion(false);
        return;
      }
      const ep = parseFloat(entryVal);
      const lp = parseFloat(lastPrice);
      if (isNaN(ep) || isNaN(lp) || lp === 0) {
        setShowMarketSuggestion(false);
        return;
      }
      const pctDiff = Math.abs(ep - lp) / lp * 100;
      setShowMarketSuggestion(pctDiff <= 2);
    };

    checkEntryPrice(value);
  };

  const handleStopLossChange = (e) => {
    const value = e.target.value;
    setStopLoss(value);
    // Remove automatic calculation - will calculate on submit
  };

  // Handle algorithm selection
  const handleAlgorithmChange = (val) => {
    const algo = AlgorithmOptions.find(a => a.value === val);
    if (algo && algo.risk) {
      const riskValue = algo.risk;
      setTradeRisk(riskValue);
      form.setFieldsValue({ tradeRisk: riskValue });
    } else {
      // If algorithm doesn't have risk value, set to default 1
      setTradeRisk(1);
      form.setFieldsValue({ tradeRisk: 1 });
    }
  };

  // Handle parsing trading prompt
  const handleParsePrompt = async () => {
    const promptValue = form.getFieldValue('tradingPrompt');
    if (!promptValue || !promptValue.trim()) {
      toast.error('Please enter a trading prompt first');
      return;
    }

    setParseLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/parse-trading-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: promptValue.trim() }),
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to parse prompt');
      }

      if (data.success) {
        // Auto-fill form with parsed data
        const formData = {
          token: data.token,
          side: data.side,
          orderType: data.orderType,
          stopLoss: data.stopLoss
        };

        if (data.orderType === 'Limit' && data.entryPrice) {
          formData.entryPrice = data.entryPrice;
        }

        form.setFieldsValue(formData);
        
        // Update component states
        setSelectedToken(data.token);
        setStopLoss(data.stopLoss);
        
        // Find token in options to get last price
        const tokenObj = tokenOptions.find(t => t.value === data.token);
        if (tokenObj) {
          setLastPrice(tokenObj.lastPrice);
          calculatePercentagePrices(tokenObj.lastPrice);
        }

        toast.success('Trading prompt parsed successfully!');
      } else {
        toast.error(data.message || 'Failed to parse trading prompt');
      }
    } catch (error) {
      console.error('Error parsing prompt:', error);
      toast.error(error.message || 'Error parsing trading prompt');
    } finally {
      setParseLoading(false);
    }
  };

  // Place or edit order
  // Place or edit order
const handleFinish = async (values) => {
  const finalSide = values.side;
  const finalPositionIdx = values.positionIdx || (finalSide === 'Buy' ? 1 : 2);
  const orderType = values.orderType;
  // Get reference price based on order type
  let referencePrice;
  if (orderType === 'Market') {
    referencePrice = lastPrice;
  } else if (orderType === 'Limit') {
    referencePrice = values.entryPrice;
  }
  // Validate stop loss if provided using stopLossVerifier
  if (values.stopLoss) {
    // Special case: if user enters "0", calculate default stop loss
    if (values.stopLoss === '0') {
      const isSell = values.side === 'Sell';
      const calculatedStopLoss = (parseFloat(referencePrice) * (isSell ? 1.10 : 0.90)).toFixed(6);
      values.stopLoss = calculatedStopLoss;
    }
    
    if (!stopLossVerifier(referencePrice, values.stopLoss, values.side)) {
      return;
    }
  }
  setLoading(true);
  try {
    let payload = { ...values };
    if (payload.algorithm) {
      const selectedAlgo = AlgorithmOptions.find(a => a.value === payload.algorithm);
      payload.algorithm = selectedAlgo ? selectedAlgo.value : payload.algorithm;
    }
    if (payload.orderType === 'Limit' && !payload.entryPrice) {
      toast.error('Entry Price is required for limit orders', { position: 'top-right', autoClose: 5000 });
      setLoading(false);
      return;
    }
    if (payload.orderType === 'Market') {
      delete payload.entryPrice;
    }
    
    // Since stop loss is now mandatory, we always validate it
    if (!stopLossVerifier(referencePrice, payload.stopLoss, payload.side)) {
      setLoading(false);
      return;
    }
    // Calculate final count using the stop loss and risk percentage
    const finalTradeRisk = payload.tradeRisk || 1;
    const finalStopLoss = payload.stopLoss;
    const finalReferencePrice = parseFloat(referencePrice);
    let rawCount = null;
    let riskValue = null;
    // Always calculate count based on risk management formula
    if (finalStopLoss && finalReferencePrice && finalTradeRisk) {
      const riskAmount = ACCOUNT_VALUE * (finalTradeRisk / 100);
      const riskPerShare = Math.abs(finalReferencePrice - parseFloat(finalStopLoss));
      if (riskPerShare > 0) {
        rawCount = riskAmount / riskPerShare;
        riskValue = riskAmount; // Store the dollar risk amount (e.g., 30 for 1% of 3000)
        console.log(`Count calculation: Risk Amount: ${riskAmount}, Risk Per Share: ${riskPerShare}, Raw Count: ${rawCount}`);
      }
    }
    
    // Fallback to manual count if risk calculation fails
    if (!rawCount && count) {
      rawCount = parseFloat(count);
      console.log(`Using manual count: ${rawCount}`);
    }
    // Add risk field to payload (dollar risk amount)
    if (riskValue) {
      payload.risk = riskValue;
    } else if (finalTradeRisk) {
      payload.risk = ACCOUNT_VALUE * (finalTradeRisk / 100);
    }
    
    // Ensure we have a valid count
    if (!rawCount || rawCount <= 0) {
      toast.error('Unable to calculate order quantity. Please check your inputs.', { position: 'top-right', autoClose: 5000 });
      setLoading(false);
      return;
    }
    // --- Use normalizeQuantity for count adjustment ---
    let adjustedCount = rawCount;
    if (selectedToken && rawCount) {
      try {
        adjustedCount = await normalizeQuantity(selectedToken, rawCount);
      } catch (err) {
        toast.error('Error normalizing quantity: ' + err.message, { position: 'top-right', autoClose: 5000 });
        setLoading(false);
        return;
      }
    }
    payload.count = adjustedCount;
    
    // Store stop loss as temp_sl_input but remove it from the main payload
    const temp_sl_input = payload.stopLoss;
    delete payload.stopLoss;
    
    payload.token = selectedToken;
    payload.side = finalSide;
    payload.positionIdx = finalPositionIdx;
    let res, data;
    if (editingOrder) {
      res = await fetch(`${BASE_URL}/api/orders/${editingOrder._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          temp_sl_input // Send stop loss separately as temp_sl_input
        }),
        credentials: 'include',
      });
    } else {
      // Get selected algorithm's complete data
      const selectedAlgo = AlgorithmOptions.find(algo => algo.value === values.algorithm); // Fixed: using values.algorithm (lowercase)
      if (!selectedAlgo) {
        toast.error('Please select a valid algorithm');
        setLoading(false);
        return;
      }

      res = await fetch(`${BASE_URL}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          algorithm_id: selectedAlgo._id,
          Algorithm: selectedAlgo.value, // Send algorithm name
          algorithm_category: selectedAlgo.category, // Also send category for reference
          temp_sl_input, // Send stop loss separately as temp_sl_input
          is_dca_order: isDcaOrder, // Add is_dca_order field
          original_trader_message: values.tradingPrompt || "", // Include the trading prompt message
          ...(dcaOrder && dcaOrder.DCA_for ? { DCA_for: dcaOrder.DCA_for } : {}) // Add DCA_for field if this is a DCA order
        }),
        credentials: 'include',
      });
    }
    data = await res.json();
    if (data.bybitRes && data.bybitRes.message) {
      if (data.bybitRes.message === 'OK') {
        toast.success(
          editingOrder ? 'Order updated successfully!' : 'Order placed successfully!',
          { position: 'top-right', autoClose: 5000 }
        );

        // Show order success modal if not editing
        if (!editingOrder) {
          openOrderSuccess({
            orderId: data._id,
            symbol: selectedToken,
            side: finalSide,
            positionIdx: finalPositionIdx,
            isSuccess: true
          });
        }

          // Immediately fetch status for new orders
        if (!editingOrder && data.orderId) {
          try {
            const statusRes = await fetch(`${BASE_URL}/api/orders/status/${data.orderId}`, { 
              credentials: 'include' 
            });
            const statusData = await statusRes.json();
            if (statusData.status) {
              // Update the data object with the latest status
              data.orderStatus = statusData.status;
            }
          } catch (statusErr) {
            console.error('Error fetching order status:', statusErr);
          }
        }


      } else {
        const cleanedMessage = cleanBybitFromMessage(data.bybitRes.message) || (editingOrder ? 'Order update failed!' : 'Order failed!');
        toast.error(
          cleanedMessage,
          { position: 'top-right', autoClose: 5000 }
        );
        // Show order failure modal if not editing
        if (!editingOrder) {
          openOrderSuccess({
            symbol: selectedToken,
            side: finalSide,
            positionIdx: finalPositionIdx,
            isSuccess: false,
            errorMessage: cleanedMessage
          });
        }
      }
    } else if (data.success) {
      toast.success(
        editingOrder ? 'Order updated successfully!' : 'Order placed successfully!',
        { position: 'top-right', autoClose: 5000 }
      );

      // Show order success modal if not editing
      if (!editingOrder) {
        openOrderSuccess({
          orderId: data._id,
          symbol: selectedToken,
          side: finalSide,
          positionIdx: finalPositionIdx,
          isSuccess: true
        });
      }

        // Immediately fetch status for new orders
      if (!editingOrder && data.orderId) {
        try {
          const statusRes = await fetch(`${BASE_URL}/api/orders/status/${data.orderId}`, { 
            credentials: 'include' 
          });
          const statusData = await statusRes.json();
          if (statusData.status) {
            // Update the data object with the latest status
            data.orderStatus = statusData.status;
          }
        } catch (statusErr) {
          console.error('Error fetching order status:', statusErr);
        }
      }

    } else {
      const cleanedMessage = cleanBybitFromMessage(data.error) || (editingOrder ? 'Order update failed!' : 'Order failed!');
      toast.error(
        cleanedMessage,
        { position: 'top-right', autoClose: 5000 }
      );
      // Show order failure modal if not editing
      if (!editingOrder) {
        openOrderSuccess({
          symbol: selectedToken,
          side: finalSide,
          positionIdx: finalPositionIdx,
          isSuccess: false,
          errorMessage: cleanedMessage
        });
      }
    }
    onClose();
    if (onOrderSuccess) {
      onOrderSuccess();
    }
  } catch (error) {
    console.error('Error placing/updating order:', error);
    toast.error(
      editingOrder ? 'Order update failed!' : 'Order placement failed!',
      { position: 'top-right', autoClose: 5000 }
    );
    // Show order failure modal for catch errors (only for new orders)
    if (!editingOrder) {
      openOrderSuccess({
        symbol: selectedToken,
        side: finalSide,
        positionIdx: finalPositionIdx,
        isSuccess: false,
        errorMessage: editingOrder ? 'Order update failed!' : 'Order placement failed!'
      });
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <Modal
      open={visible}
      title={editingOrder ? 'Edit Order' : 'Place Order'}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ tradeRisk: 1 }}
      >
        {/* Trading Prompt Input */}
        {/* <Form.Item 
          name="tradingPrompt" 
          label="Enter Trading Prompt"
          style={{ width: '100%', marginBottom: 16 }}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <Input.TextArea
              placeholder="e.g., Longed AEVO at 0.1248 SI-0.119 (1% risk)"
              rows={1}
              style={{ flex: 1 }}
            />
            <Button 
              type="primary" 
              onClick={handleParsePrompt}
              loading={parseLoading}
              style={{ alignSelf: 'flex-end', height: '34px' }}
            >
              Get
            </Button>
          </div>
        </Form.Item> */}

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <Form.Item 
            name="token" 
            label="Token" 
            rules={[{ required: true, message: 'Please select a token' }]} 
            style={{ flex: 1 }}
          >
            <Select
              showSearch
              placeholder="Select a token"
              options={tokenOptions}
              value={selectedToken}
              onChange={handleTokenChange}
              filterOption={(input, option) => 
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              loading={tokenLoading}
              onDropdownVisibleChange={open => {
                if (open && tokenOptions.length === 0) fetchTokenOptions();
              }}
              notFoundContent={tokenLoading ? <Spin size="small" /> : null}
            />
          </Form.Item>

          {/* Algorithm Field with Add New Button in Label */}
          <Form.Item
            name="algorithm"
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Algorithm</span>
                {/* <Button size="small" type="primary" onClick={handleAddAlgorithm} style={{ marginLeft: 8 }}>
                  Add New
                </Button> */}
              </div>
            }
            style={{ flex: 1 }}
            rules={[{ required: true, message: 'Please select algorithm' }]}
          >
            <Select
              placeholder="Select Algorithm"
              options={AlgorithmOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleAlgorithmChange}
            />
          </Form.Item>
        </div>

        {lastPrice && (
          <div style={{ marginBottom: 5, color: '#888' }}>
            Last Price: <b>{lastPrice}</b>
            {/* {ninetyPercentPrice && (
              <span style={{ marginLeft: 20 }}>
                90%: <b style={{ color: '#ff4d4f' }}>{ninetyPercentPrice}</b>
              </span>
            )}
            {ninetyFivePercentPrice && (
              <span style={{ marginLeft: 20 }}>
                95%: <b style={{ color: '#52c41a' }}>{ninetyFivePercentPrice}</b>
              </span>
            )} */}
          </div>
        )}

       
          {/* <Space  className='pnl-numbers' style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Form.Item 
            name="totalValue" 
            label="Total Order Value" 
            style={{ flex: 1, marginRight: 8 }}
           
          >
            <InputNumber 
              min={0.0001} 
              step={0.0001} 
              style={{ width: '100%' }} 
              value={totalValue}
              onChange={handleTotalValueChange}
              placeholder="Total value"
            />
          </Form.Item>
          
          <Form.Item 
            name="count" 
            label="Count" 
            style={{ flex: 1 }}
          >
            <InputNumber 
              min={0.0001} 
              step={0.0001} 
              style={{ width: '100%' }} 
              value={count}
              onChange={handleCountChange}
              placeholder="Quantity"
            />
          </Form.Item>
        </Space>     */}

        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item 
            name="side" 
            label="Side" 
            rules={[{ required: true, message: 'Please select a side' }]}
            style={{ width: '50%' }}
          > 
            <Select
              placeholder="Select Side"
              onChange={val => {
                setPositionIdx(val === 'Buy' ? 1 : 2);
              }}
              allowClear
            >
              {sideOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item 
            name="orderType" 
            label="Order Type" 
            rules={[{ required: true, message: 'Please select order type' }]}
            style={{ width: '50%' }}
          > 
            <Select 
              onChange={handleOrderTypeChange}
              placeholder="Select Order Type"
              allowClear
            >
              {orderTypeOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          shouldUpdate={(prev, curr) => prev.orderType !== curr.orderType}
          noStyle
        >
          {({ getFieldValue }) =>
            getFieldValue('orderType') === 'Limit' ? (
                <Form.Item 
                  name="entryPrice" 
                  label={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Entry Price</span>
                      {showMarketSuggestion && (
                        <span style={{ color: '#faad14', fontWeight: 600, marginLeft: 8 }}>
                          Market order suggested
                        </span>
                      )}
                    </div>
                  }
                  rules={[{ required: true, message: 'Entry price is required for limit orders' }]}
                >
                  <Input placeholder="Enter entry price" type='number' onChange={handleEntryPriceChange} autoComplete="off" autoFill="off" />
                </Form.Item>
            ) : null
          }
        </Form.Item>

        <div style={{ display: 'flex', gap: 12 }}>
         
          <Form.Item 
            name="stopLoss" 
            label="Stop Loss" 
            style={{ width: '100%' }}
            rules={[{ required: true, message: 'Stop loss is required' }]}
          >
            <Input 
              // placeholder="Will default to 95% if empty" 
              onChange={handleStopLossChange}
              autoComplete="off"
              autoFill="off"
              type='number'
            />
          </Form.Item>
        </div>

        {!editingOrder && (
          <Form.Item 
            name="isDcaOrder" 
            label="Is DCA?" 
            rules={[{ required: true, message: 'DCA  is required' }]}
          >
            <Select 
              placeholder="Select value"
              value={isDcaOrder}
              onChange={(value) => setIsDcaOrder(value)}
              style={{ width: '100%' }}
            >
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        )}
           {/* <Form.Item 
            name="tradeRisk" 
            label="Trade Risk (%)" 
            style={{ width: '50%' }}
          >
            <InputNumber 
              min={0.1} 
              max={5} 
              step={0.1} 
              style={{ width: '100%' }} 
              placeholder="Default: 1"
              value={tradeRisk}
              onChange={handleTradeRiskChange}
            />
          </Form.Item> */}

        <Modal
          open={algoModalOpen}
          title="Add New Algorithm"
          onCancel={handleAlgoModalCancel}
          footer={null}
          destroyOnClose
        >
          <Form form={algoForm} layout="vertical" onFinish={handleAlgoFinish}>
            <Form.Item
              name="name"
              label="Algorithm Name"
              rules={[
                { required: true, message: 'Please enter a name' },
                { pattern: /^[a-zA-Z0-9]+$/, message: 'No spaces or special characters' },
              ]}
            >
              <Input placeholder="e.g. Woods" maxLength={20} autoFocus />
            </Form.Item>
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please enter a category' }]}
            >
              <Input placeholder="e.g. Trend, Scalping, etc." maxLength={30} />
            </Form.Item>
            <Form.Item
              name="risk"
              label="Risk (0.01 - 5)"
              rules={[
                { required: true, message: 'Please enter a risk value' },
                { type: 'number', min: 0.01, max: 5, message: 'Risk must be between 0.01 and 5' }
              ]}
            >
              <InputNumber min={0.01} max={5} step={0.01} style={{ width: '100%' }} placeholder="e.g. 1.5" />
            </Form.Item>
            {algoError && <div style={{ color: 'red', marginBottom: 8 }}>{algoError}</div>}
            <Form.Item style={{ marginBottom: 0 }}>
              <Button htmlType="submit" type="primary" loading={algoLoading} block>
                Add Algorithm
              </Button>
            </Form.Item>
          </Form>
        </Modal>

            <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              style={{ background: 'black', color: 'white', borderRadius: 20, paddingLeft: 18, paddingRight: 18 }}
              loading={loading}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  {editingOrder ? 'Updating...' : 'Placing...'}
                </>
              ) : (
                editingOrder ? 'Update Order' : 'Place Order'
              )}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrderForm;

        {/* <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              style={{ background: 'black', color: 'white' }}
              loading={loading}
            ></Button> */}